import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { NavIcon } from "./NavIcon";
import {
  hasSubMenu,
  getModulePrimaryHref,
  type NavigationItem,
} from "@/lib/config/navigationConfig";

interface ErpSidebarModuleRowProps {
  module: NavigationItem;
  isActive: boolean;
  isProvisioned: boolean;
  onDrillIn: () => void;
  collapsed?: boolean;
  /** When true, renders the Notion-style flat row (outline icon, rounded-md highlight). */
  insetCard?: boolean;
}

export function ErpSidebarModuleRow({
  module,
  isActive,
  isProvisioned,
  onDrillIn,
  collapsed = false,
  insetCard = false,
}: ErpSidebarModuleRowProps) {
  const canDrill = hasSubMenu(module);
  const disabled = module.requiresProvisioning && !isProvisioned;

  // Collapsed: icon-only, always links to primary href
  if (collapsed) {
    const href = getModulePrimaryHref(module);
    return (
      <Link
        to={href}
        className={cn(
          "flex items-center justify-center w-10 h-10 rounded",
          "transition-colors duration-150",
          disabled
            ? "opacity-50 pointer-events-none"
            : isActive
              ? "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white"
              : "text-gray-400 hover:bg-white/40 hover:text-gray-600"
        )}
        title={module.name}
      >
        <NavIcon icon={module.icon} className={cn("w-[17px] h-[17px]", isActive ? "text-gray-700 dark:text-gray-300" : "text-gray-400")} />
      </Link>
    );
  }

  // Notion-style flat row — outline icon, rounded-md highlight
  if (insetCard) {
    const content = (
      <>
        <NavIcon
          icon={module.icon}
          className={cn(
            "w-[17px] h-[17px] shrink-0",
            isActive ? "text-[#0F0F10] dark:text-gray-100" : "text-[#0F0F10]/55 dark:text-gray-500"
          )}
        />
        <span
          className={cn(
            "text-[14px] truncate flex-1",
            isActive
              ? "text-[#0F0F10] dark:text-gray-100 font-medium"
              : "text-[#0F0F10]/60 dark:text-gray-400"
          )}
        >
          {module.name}
        </span>
        {canDrill && (
          <ChevronRight
            className={cn(
              "w-3.5 h-3.5 shrink-0",
              isActive ? "text-gray-900/40 dark:text-gray-100/40" : "text-gray-900/30 dark:text-gray-100/30"
            )}
          />
        )}
      </>
    );

    const baseClasses = cn(
      "flex items-center gap-2.5 h-[38px] px-2.5 rounded-sm w-full text-left transition-colors cursor-pointer",
      disabled
        ? "opacity-50 pointer-events-none"
        : isActive
          ? "bg-gray-100 dark:bg-gray-800"
          : "hover:bg-gray-100/50 dark:hover:bg-gray-800/50"
    );

    if (!canDrill) {
      return (
        <Link to={getModulePrimaryHref(module)} className={baseClasses}>
          {content}
        </Link>
      );
    }

    return (
      <button type="button" onClick={onDrillIn} className={baseClasses} disabled={disabled}>
        {content}
      </button>
    );
  }

  // Legacy expanded: flat row style (kept for drill-down sub-views)
  const content = (
    <>
      <NavIcon
        icon={module.icon}
        className={cn(
          "w-4 h-4 shrink-0",
          isActive
            ? "text-gray-900 dark:text-white"
            : "text-gray-400 dark:text-gray-500"
        )}
      />
      <span className="truncate">{module.name}</span>
      {canDrill && (
        <ChevronRight className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 shrink-0 ml-auto" />
      )}
    </>
  );

  const baseClasses = cn(
    "flex items-center gap-3 h-9 pl-3 pr-2 w-full text-left",
    "text-sm transition-colors duration-150",
    disabled
      ? "opacity-50 pointer-events-none text-gray-400 dark:text-gray-600"
      : isActive
        ? "bg-white/60 dark:bg-gray-800 shadow-sm font-medium text-gray-900 dark:text-white"
        : "text-gray-600 dark:text-gray-400 hover:bg-white/40 dark:hover:bg-gray-800/50"
  );

  if (!canDrill) {
    return (
      <Link to={getModulePrimaryHref(module)} className={baseClasses}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onDrillIn}
      className={baseClasses}
      disabled={disabled}
    >
      {content}
    </button>
  );
}
